import type { AnalyticsProperties } from "@/lib/analytics/types";

/**
 * Canonical event names — same string in Amplitude and Facebook (Pixel + CAPI).
 * Meta standard events use official names; others are custom (`Login`, `ToolRun`).
 */
export const AnalyticsEvent = {
  PageView: "PageView",
  CompleteRegistration: "CompleteRegistration",
  Login: "Login",
  InitiateCheckout: "InitiateCheckout",
  Purchase: "Purchase",
  ToolRun: "ToolRun",
} as const;

export type AnalyticsEventName =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

/** Meta Pixel / CAPI standard event names (use `fbq('track', …)` / CAPI standard). */
export type MetaStandardEvent =
  | "PageView"
  | "CompleteRegistration"
  | "InitiateCheckout"
  | "Purchase";

export type MetaEventMapping =
  | { kind: "standard"; name: MetaStandardEvent }
  | { kind: "custom"; name: string };

const META_STANDARD_EVENTS = new Set<string>([
  AnalyticsEvent.PageView,
  AnalyticsEvent.CompleteRegistration,
  AnalyticsEvent.InitiateCheckout,
  AnalyticsEvent.Purchase,
]);

/** Classify for Pixel `track` vs `trackCustom` (name is already canonical). */
export function toMetaEvent(name: string): MetaEventMapping {
  if (META_STANDARD_EVENTS.has(name)) {
    return { kind: "standard", name: name as MetaStandardEvent };
  }
  return { kind: "custom", name };
}

export type PageViewProps = {
  path: string;
  title?: string;
};

export type AuthMethod = "password" | "google" | "apple";

export type AuthEventProps = {
  method: AuthMethod;
};

export type InitiateCheckoutProps = {
  product_type: "subscription" | "certificate";
  value?: number;
  currency?: string;
  variant_id?: number;
};

export type PurchaseProps = {
  product_type: "subscription" | "certificate";
  value?: number;
  currency?: string;
  order_id?: string | number;
  transaction_id?: string;
};

export type ToolRunProps = {
  tool_slug: string;
  tool_name?: string;
  is_authenticated: boolean;
};

/** Narrow helpers so call-sites stay typed. */
export function propsOf(
  _name: AnalyticsEventName,
  props: AnalyticsProperties,
): AnalyticsProperties {
  return props;
}
